import {
  getUsersInternal,
  Roles,
  User,
} from "@aura-frontend/omnibus-az-client";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Icons, Input, Label, Table, useDebounce } from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { adminRolesHierarchy, UserRoleOptions } from "@/types/roles";

const usersInternalInfiniteQueryOptions = (search?: string) =>
  infiniteQueryOptions({
    queryKey: ["getUsersInternal", search],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getUsersInternal({
        query: { continue: pageParam, search },
        signal,
        throwOnError: true,
      });
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.continue,
  });

const Admins = () => {
  const [rawSearchValue, setSearchValue] = useState("");
  const searchValue = useDebounce(rawSearchValue, 250, false);
  const [sortingState, setSortingState] = useState<SortingState>([]);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(usersInternalInfiniteQueryOptions(searchValue));

  const users = useMemo(
    () => data?.pages.flatMap((page) => page.users) ?? [],
    [data?.pages],
  );

  const columns: ColumnDef<User>[] = useMemo(
    () => [
      {
        accessorKey: "givenName",
        header: "First Name",
      },
      {
        accessorKey: "familyName",
        header: "Last Name",
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row: { original: row } }) => {
          return row.emails?.[0] ?? "";
        },
      },
      {
        accessorKey: "roles",
        header: "Role",
        cell: ({ row: { original: row } }) => {
          const userRole = adminRolesHierarchy.find((role: string) =>
            row.roles.includes(role as keyof typeof UserRoleOptions),
          ) as Roles;

          return UserRoleOptions[userRole];
        },
      },
    ],
    [],
  );

  return (
    <div className="relative flex flex-col pt-5">
      <div className="flex items-end justify-between px-5">
        <div className="flex space-x-4">
          <div>
            <Label htmlFor="operator-search" className="mb-2 text-sm">
              Search
            </Label>

            <Input
              className="w-60"
              value={rawSearchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Look for an admin"
              isClearable
              onClear={() => setSearchValue("")}
              iconRight={
                <Icons.MagnifyingGlass className="h-5 w-5 text-placeholder" />
              }
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <Table
          enableHorizontalScroll
          variant="infinite-scroll"
          hasMore={hasNextPage}
          isFetching={isLoading || isFetchingNextPage}
          data={users}
          columns={columns}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          rowClassName={"shadow-glow-tiny rounded"}
          rowsWrapperClassName="gap-y-4"
          tableWrapperClassName="overflow-auto px-5 py-1"
        />
      </div>
    </div>
  );
};

export { Admins };
